import { V3Models } from "@istari/istari-client";
import { useCallback } from "react";

import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { buildReferencedFileName } from "~/ai-chat/model/chat-message";
import { extensionOf, inferMimeType, type SaveResourceAction } from "~/ai-chat/model/save-resource-action";
import { useAssignInfosecLevel } from "~/api/useInfosecLevels";
import { useCreateResource } from "~/resource/data-source/use-create-resource";

export interface SavedChatResource {
  resourceId: string;
  fileRevisionId: string;
  infosecApplied: boolean;
}

interface SaveResourceActionApi {
  save: (action: SaveResourceAction, infosecLevelId?: string) => Promise<SavedChatResource>;
  isPending: boolean;
}

export function useSaveResourceAction(): SaveResourceActionApi {
  const { addReferencedFiles } = useAiChat();
  const createResource = useCreateResource();
  const assignInfosecLevel = useAssignInfosecLevel();

  const save = useCallback(
    async (action: SaveResourceAction, infosecLevelId?: string): Promise<SavedChatResource> => {
      const file = new File([action.content], action.fileName, { type: inferMimeType(action.fileName) });
      // Model, not File: the Files explorer lists only Model/Artifact resources
      // (FILES_EXPLORER_RESOURCE_TYPES), and manual uploads default to Model —
      // a "file"-typed resource would be invisible in the library.
      const resource = await createResource.mutateAsync({
        file,
        displayName: action.displayName,
        description: action.description,
        resourceType: V3Models.ResourceTypeDto.Model,
      });

      let infosecApplied = true;
      if (infosecLevelId) {
        // The resource already exists at this point, so a failed level assignment
        // must not reject the save — a retry from the caller would create a
        // duplicate resource. The caller surfaces the miss on the save card
        // (toasts render at the app root and are invisible from a popped-out chat).
        try {
          await assignInfosecLevel.mutateAsync({ type: "model", id: resource.resourceId, infosecLevelId });
        } catch {
          infosecApplied = false;
        }
      }

      addReferencedFiles([
        {
          resourceId: resource.resourceId,
          name: buildReferencedFileName(action.displayName, extensionOf(action.fileName)),
          fileRevisionId: resource.fileRevisionId,
          origin: "chat-save",
        },
      ]);
      return { resourceId: resource.resourceId, fileRevisionId: resource.fileRevisionId, infosecApplied };
    },
    [createResource, assignInfosecLevel, addReferencedFiles],
  );

  return { save, isPending: createResource.isPending || assignInfosecLevel.isPending };
}
